import { ReparoApi } from './index';

export type B2bPoTrackerStatus =
    | 'OPEN' | 'ACTED' | 'DISPATCHED' | 'FULFILLED' | 'REJECTED' | 'EXPIRED' | 'CANCELLED';

export interface B2bPoChannelRollup {
    channelAbbreviationKey: string;
    open: number;
    breaching: number;
    acted: number;
    dispatched: number;
}

export interface B2bPoOverview {
    open: number;
    breaching: number;
    acted: number;
    dispatched: number;
    onTime: number;
    channels: B2bPoChannelRollup[];
    computedAt: string;
}

export interface B2bPoTracker {
    id: string;
    channelAbbreviationKey: string;
    purchaseOrderId: string;
    poDisplayCode: string;
    destinationWarehouseId: string | null;
    /** Destination FC display name. Null when the PO has no FC yet. */
    destinationWarehouseName: string | null;
    /** Reparo action status (tracker lifecycle). */
    status: B2bPoTrackerStatus;
    /** Live PO status from the WMS sync (Created / Scheduled / Shipped / Fulfilled / …). */
    poStatus: string | null;
    poReceivedAt: string;
    actByAt: string;
    isBreaching: boolean;
    actedAt: string | null;
    expectedDispatchAt: string | null;
    dispatchedAt: string | null;
    approvedQty: number;
    dispatchedQty: number;
    /** PO expiry date (yyyy-MM-dd). Drives the Open vs Expired split. Null when unset. */
    expiryDate: string | null;
    /** Distinct SKUs on the PO. */
    skuCount: number;
    /** Total units ordered across all SKUs on the PO. */
    totalUnits: number;
    /** Order value before tax, summed over the PO's lines; null when no line is priced. */
    orderValue: number | null;
}

export const getB2bPoOverview = async (): Promise<B2bPoOverview> =>
    ReparoApi({ method: 'GET', url: '/b2b-po/overview', body: null });

export const getB2bPoChannelPos = async (channelKey: string): Promise<B2bPoTracker[]> =>
    ReparoApi({ method: 'GET', url: `/b2b-po/channels/${encodeURIComponent(channelKey)}/pos`, body: null });

export interface B2bPoLine {
    productId: string;
    productName: string;
    quantity: number;
    /** Before tax, as the order states them; null when the line is not priced. */
    unitPrice: number | null;
    lineTotal: number | null;
}

export const getB2bPoLines = async (purchaseOrderId: string): Promise<B2bPoLine[]> =>
    ReparoApi({ method: 'GET', url: `/b2b-po/pos/${encodeURIComponent(purchaseOrderId)}/lines`, body: null });

/** Assigns a destination FC to a PO that arrived without one (per-FC PO-driven flow). */
export const assignB2bPoFc = async (purchaseOrderId: string, warehouseId: string): Promise<void> =>
    ReparoApi({
        method: 'POST',
        url: `/b2b-po/pos/${encodeURIComponent(purchaseOrderId)}/fc`,
        body: JSON.stringify({ warehouseId }),
    });
